/**
 * 장바구니 관련 API 서비스
 *
 * API 클라이언트 인스턴스를 인자로 받아 호출 (DI 패턴)
 * shared 레이어: config, workspace-pkg만 import 가능
 */

import type { ApiClient } from "@/shared/api/client";
import type { ApiError } from "@/shared/api/types";

// ── 장바구니 아이템 ──

export interface CartItem {
  idOptionDetail: number;
  goodsCount: number;
  /** 사용자가 본 옵션 라벨 — 백엔드가 OptionDetail.VariantList 로 자동 생성하는
   *  optionsName 보다 우선 저장된다 (carts.optionsName json 컬럼). */
  optionsName?: string;
}

/** ✅ curl verified 2026-04-12 — GET /carts 실제 응답 구조 */
export interface CartItemProduct {
  id: number;
  name: string;
  idBrand: number;
  brandName: string;
  imagePath: string;
  goodsPrice: number;
  fixedPrice: number;
  isOnlineSale: boolean;
  minOrderCnt: number;
  prodStatus: string;
}

export interface CartEntry {
  cart: {
    id: number;
    idUser: number;
    idProduct: number;
    idOptionDetail: number;
    goodsCount: number;
    combinationId: string;
    createdAt: string;
    updatedAt: string;
  };
  product: {
    product: CartItemProduct;
    options: {
      id: number;
      idProduct: number;
      code: string;
      name: string;
      isRequire: boolean;
      variant: {
        id: number;
        idProductOption: number;
        code: string;
        name: string;
        addedPrice: number;
        isOnSale: boolean;
      }[];
    }[];
    options_details: {
      id: number;
      VariantList: string; // JSON array of variant codes e.g. '["O20260317..."]'
      price: number;
      /** MOQ authority used by the legacy order backend. Sequelize BOOLEAN may be boolean or 0/1. */
      is_require: boolean | number | string | null;
      isOnSale: number;
    }[];
  };
}

// ── 장바구니 추가 요청 ──

export interface AddToCartRequest {
  directCart: "y" | "n";
  idProduct: number;
  cartInfo: CartItem[];
}

// ── 장바구니 수량 변경 요청 ──

export interface UpdateCartItemRequest {
  goodsCount: number;
}

// ── 복수 삭제 요청 ──

export interface DeleteMultipleCartsRequest {
  cartIds: string[];
}

// ── 장바구니 수량 응답 ──

export interface CartCountResponse {
  cartcount: number;
}

// ── 재고 확인 응답 ──

/** ✅ verified against legacy backend carts.router.js — GET /carts/available */
export interface CartAvailabilityError {
  cartId: number;
  productId?: number;
  idOptionDetail?: number;
  name: string;
  option?: string;
  /** 백엔드 원문: "product soldout" | "option soldout" | "product changed" | "option changed" | "온라인 판매 상품이 아닙니다." */
  reason: string;
}

export interface CartAvailabilityResponse {
  errorIdProduct: CartAvailabilityError[];
}

// ── 배송비 조회 응답 ──

/** ✅ verified against legacy backend 2026-04-19 — GET /carts/delivery/{zipCd} */
export interface CartDeliveryFeeResponse {
  deliveryfee: number;
}

// ── 서비스 팩토리 ──

export function createCartService(client: ApiClient) {
  return {
    /** 장바구니 목록 조회 GET /carts — 빈 장바구니 시 백엔드 400 반환을 빈 배열로 처리 */
    getCarts: async () => {
      try {
        return await client.get<CartEntry[]>("/carts");
      } catch (error) {
        // 백엔드가 빈 장바구니를 400 "No Matching Cart"로 반환한다.
        // 네트워크/5xx까지 빈 장바구니로 숨기면 서버에 반영된 최신 수량을
        // 잃고 stale checkout을 만들 수 있으므로 해당 계약만 예외 처리한다.
        const apiError = error as Partial<ApiError>;
        if (apiError.status === 400 && apiError.message === "No Matching Cart") {
          return {
            data: [] as CartEntry[],
            status: 200,
            message: "ok",
          };
        }
        throw error;
      }
    },

    /** 장바구니 추가 POST /carts — 백엔드는 plain text "Create Cart Success" 반환 */
    addToCart: (data: AddToCartRequest) => client.post<string>("/carts", data),

    /** 장바구니 아이템 수량 변경 PUT /carts/item/{cartId} — plain text "Update Cart Success" */
    updateCartItem: (cartId: number, data: UpdateCartItemRequest) =>
      client.put<string>(`/carts/item/${cartId}`, data),

    /** 장바구니 아이템 삭제 DELETE /carts/{cartId} */
    deleteCartItem: (cartId: number) => client.del<null>(`/carts/${cartId}`),

    /** 장바구니 복수 삭제 DELETE /carts/multiple/delete */
    deleteMultipleCarts: (data: DeleteMultipleCartsRequest) =>
      client.del<null>("/carts/multiple/delete", data),

    /** 장바구니 수량 조회 GET /carts/cartcount */
    getCartCount: () => client.get<CartCountResponse>("/carts/cartcount"),

    /** 장바구니 재고 확인 GET /carts/available — errorIdProduct가 비어있으면 모두 구매 가능 */
    getAvailability: (checkedIds: string) =>
      client.get<CartAvailabilityResponse>("/carts/available", {
        params: { checkedIds },
      }),

    /** 배송비 조회 GET /carts/delivery/{zipCode} */
    getDeliveryFee: (zipCode: string) =>
      client.get<CartDeliveryFeeResponse>(`/carts/delivery/${zipCode}`),
  };
}
